'use client';

import { Button, cn, Input, Label } from '@up2stay/up2promo-ui';
import { Plus, X } from 'lucide-react';
import { useId, useState } from 'react';

import { FeedbackRecipientImportPreview } from '@/core/components/feedback/feedback-recipient-import-preview';
import { useFeedbackRecipientImport } from '@/core/components/feedback/use-feedback-recipient-import';
import {
  isValidFeedbackEmail,
  normalizeFeedbackEmail,
  parseFeedbackRecipients,
  type FeedbackRecipient,
} from '@/core/lib/parse-feedback-recipients';

export type FeedbackRecipientInputLabels = {
  name: string;
  namePlaceholder: string;
  email: string;
  emailPlaceholder: string;
  add: string;
  empty: string;
  count: string;
  invalidEmail: string;
  duplicateEmail: string;
  remove: string;
  pasteHint: string;
  importPreviewTitle: string;
  importPreviewConfirm: string;
  importPreviewCancel: string;
  importPreviewReady: string;
  importPreviewInvalid: string;
  importPreviewDuplicate: string;
  importPreviewOverQuota: string;
  noneReady: string;
};

type FeedbackRecipientInputProps = {
  recipients: FeedbackRecipient[];
  onRecipientsChange: (recipients: FeedbackRecipient[]) => void;
  labels: FeedbackRecipientInputLabels;
  remainingSlots: number | null;
  /** Form-level error (e.g. empty list on submit). */
  error?: string | null;
};

export function FeedbackRecipientInput({
  recipients,
  onRecipientsChange,
  labels,
  remainingSlots,
  error,
}: FeedbackRecipientInputProps) {
  const nameId = useId();
  const emailId = useId();
  const listErrorId = useId();
  const [draftName, setDraftName] = useState('');
  const [draftEmail, setDraftEmail] = useState('');
  const [emailError, setEmailError] = useState<string | null>(null);
  const importFlow = useFeedbackRecipientImport({
    recipients,
    onRecipientsChange,
    remainingSlots,
    noneReadyMessage: labels.noneReady,
  });

  const existingEmails = new Set(
    recipients.map((r) => normalizeFeedbackEmail(r.email)),
  );

  const addSingle = () => {
    const email = normalizeFeedbackEmail(draftEmail);
    if (!email) {
      setEmailError(null);
      return;
    }
    if (!isValidFeedbackEmail(email)) {
      setEmailError(labels.invalidEmail);
      return;
    }
    if (existingEmails.has(email)) {
      setEmailError(labels.duplicateEmail);
      return;
    }
    if (remainingSlots != null && remainingSlots <= 0) {
      setEmailError(labels.noneReady);
      return;
    }
    onRecipientsChange([
      ...recipients,
      { email, name: draftName.trim() || undefined },
    ]);
    setDraftEmail('');
    setDraftName('');
    setEmailError(null);
  };

  const handlePaste = (event: React.ClipboardEvent<HTMLInputElement>) => {
    const text = event.clipboardData.getData('text');
    const looksBulk = /[\n,;]/.test(text) || /^.+<[^>]+>$/.test(text.trim());
    if (!looksBulk) return;

    event.preventDefault();
    const parsed = parseFeedbackRecipients(text);
    if (parsed.length === 0) {
      setEmailError(labels.invalidEmail);
      return;
    }
    importFlow.requestImport(parsed);
    setDraftEmail('');
    setDraftName('');
    setEmailError(null);
  };

  const removeAt = (index: number) => {
    onRecipientsChange(recipients.filter((_, i) => i !== index));
  };

  return (
    <div
      className={cn(
        'space-y-3 rounded-md',
        error && 'ring-1 ring-destructive/40',
      )}
      aria-invalid={error ? true : undefined}
      aria-describedby={error ? listErrorId : undefined}
    >
      <div className="grid gap-2 sm:grid-cols-[minmax(0,0.9fr)_minmax(0,1.1fr)_auto] sm:items-end">
        <div className="space-y-1.5">
          <Label htmlFor={nameId}>{labels.name}</Label>
          <Input
            id={nameId}
            value={draftName}
            autoComplete="name"
            placeholder={labels.namePlaceholder}
            onChange={(event) => setDraftName(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                event.preventDefault();
                addSingle();
              }
            }}
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor={emailId}>{labels.email}</Label>
          <Input
            id={emailId}
            type="email"
            inputMode="email"
            autoComplete="email"
            value={draftEmail}
            placeholder={labels.emailPlaceholder}
            aria-invalid={emailError ? true : undefined}
            aria-describedby={emailError ? `${emailId}-error` : undefined}
            onChange={(event) => {
              setDraftEmail(event.target.value);
              if (emailError) setEmailError(null);
            }}
            onPaste={handlePaste}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                event.preventDefault();
                addSingle();
              }
            }}
          />
        </div>
        {/* Own grid row on desktop, so the error keeps name, email and the
            button aligned (DEV-769); right under the email field on mobile. */}
        {emailError ? (
          <p
            id={`${emailId}-error`}
            className="text-xs text-destructive sm:order-last sm:col-span-3"
            role="alert"
          >
            {emailError}
          </p>
        ) : null}
        <Button
          type="button"
          variant="outline"
          className="w-full sm:w-auto"
          onClick={addSingle}
        >
          <Plus className="size-4" />
          {labels.add}
        </Button>
      </div>

      <p className="text-xs text-muted-foreground">{labels.pasteHint}</p>

      {recipients.length === 0 ? (
        <p
          className={cn(
            'text-sm',
            error ? 'text-destructive' : 'text-muted-foreground',
          )}
        >
          {labels.empty}
        </p>
      ) : (
        <ul className="space-y-1" aria-live="polite">
          {recipients.map((recipient, index) => (
            <li
              key={`${recipient.email}-${index}`}
              className="flex items-center gap-2 rounded-md border border-border/60 bg-muted/20 px-2.5 py-1.5"
            >
              <span className="min-w-0 flex-1 truncate text-sm">
                {recipient.name ? (
                  <>
                    <span className="font-medium">{recipient.name}</span>
                    <span className="text-muted-foreground">
                      {' '}
                      · {recipient.email}
                    </span>
                  </>
                ) : (
                  recipient.email
                )}
              </span>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="size-7 shrink-0"
                aria-label={labels.remove}
                onClick={() => removeAt(index)}
              >
                <X className="size-3.5" />
              </Button>
            </li>
          ))}
        </ul>
      )}

      {error ? (
        <p id={listErrorId} className="text-xs text-destructive" role="alert">
          {error}
        </p>
      ) : null}

      <FeedbackRecipientImportPreview
        preview={importFlow.preview}
        labels={{
          title: labels.importPreviewTitle,
          confirm: labels.importPreviewConfirm,
          cancel: labels.importPreviewCancel,
          ready: labels.importPreviewReady,
          invalid: labels.importPreviewInvalid,
          duplicate: labels.importPreviewDuplicate,
          overQuota: labels.importPreviewOverQuota,
        }}
        onConfirm={importFlow.confirmPreview}
        onCancel={importFlow.cancelPreview}
      />

      <p className="text-xs text-muted-foreground">
        {labels.count.replace('{count}', String(recipients.length))}
      </p>
    </div>
  );
}
